import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { formatCurrency, formatCommissionRate } from '../../../utils/formatters';
import { CommissionTier } from '../../../types/commissionTier';
import { calculateTierCommission } from '../../../utils/commissionTierUtils';

interface Props {
  isMerged: boolean;
  rate: number;
  value: number;
  creditCommissionValue: number;
  totalCommission: number;
  showCommission: boolean;
  showCreditCommission: boolean;
  activeTier?: CommissionTier | null;
}

export const PricingCommissionCards = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      isMerged,
      rate,
      value,
      creditCommissionValue,
      totalCommission,
      showCommission,
      showCreditCommission,
      activeTier,
    },
    ref
  ) => {
    const effectiveRate = activeTier ? activeTier.porcentaje : null;
    const baseRateLabel = formatCommissionRate(rate);
    const rateLabel = effectiveRate != null ? `${effectiveRate}%` : baseRateLabel;
    const effectiveSaleComm = activeTier
      ? (activeTier.valor_comision ?? calculateTierCommission(activeTier.precio, activeTier.porcentaje))
      : value;
    const effectiveCreditComm = activeTier ? activeTier.precio * 0.004 : creditCommissionValue;
    const effectiveTotalComm = effectiveSaleComm + effectiveCreditComm;
    const isInitial = !activeTier || activeTier.es_inicial || (activeTier.precio && effectiveSaleComm === value);

    return (
      <div ref={ref} className="w-full">
        <AnimatePresence mode="popLayout">
          {isMerged ? (
            <motion.div
              key="merged-commission"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
              className="bg-gradient-to-br from-indigo-50 to-pink-50 p-5 rounded-2xl border border-indigo-100 shadow-sm transform hover:scale-[1.01] transition-all"
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-[0.16em] text-indigo-900">
                  Comisión Total
                </p>
                <span className="bg-indigo-100 text-indigo-800 text-[10px] sm:text-xs font-black uppercase px-2.5 py-1 rounded-full tracking-wider border border-indigo-200 shrink-0">
                  {isInitial ? 'Inicial' : 'Tramo'}: {rateLabel} + 0.4%
                </span>
              </div>
              <p className="text-xl xs:text-2xl md:text-xl lg:text-2.5xl xl:text-3xl font-black text-indigo-950 tracking-tight break-all font-mono">
                {formatCurrency(effectiveTotalComm)}
              </p>
              <div className="mt-3 pt-3 border-t border-indigo-100/50 text-xs font-medium text-slate-500 space-y-1.5">
                <div className="flex justify-between">
                  <span>Venta ({rateLabel}):</span>
                  <span className="font-extrabold text-slate-700 font-mono">{formatCurrency(effectiveSaleComm)}</span>
                </div>
                {!isInitial && (
                  <div className="flex justify-between text-[10px] text-slate-400">
                    <span>Inicial ({baseRateLabel}):</span>
                    <span className="font-bold text-slate-500 font-mono">{formatCurrency(value)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Crédito Aliados (0.4%):</span>
                  <span className="font-extrabold text-pink-600 font-mono">{formatCurrency(effectiveCreditComm)}</span>
                </div>
              </div>
              <p className="text-xs text-pink-700 uppercase font-black tracking-wider mt-3 block leading-normal">
                ✨ ¡Aumenta tus ganancias uniendo referido + venta!
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="separated-commission"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
              className="space-y-4 w-full"
            >
              {showCommission && (
                <div className="bg-sky-50/70 p-4 rounded-2xl border border-sky-100 shadow-sm transform hover:scale-[1.01] transition-all">
                  <div className="flex items-center justify-between mb-1.5 gap-2">
                    <p className="text-xs sm:text-sm font-black uppercase tracking-[0.16em] text-sky-900">
                      Comisión por Venta
                    </p>
                    <span className="bg-sky-200/80 text-sky-900 text-[10px] sm:text-xs font-black uppercase px-2.5 py-1 rounded-full tracking-wider border border-sky-300 shrink-0">
                      {isInitial ? 'Inicial: ' : 'Tramo: '}{rateLabel}
                    </span>
                  </div>
                  <p className="text-xl xs:text-2xl md:text-xl lg:text-2xl font-black text-sky-800 tracking-tight break-all font-mono">
                    {formatCurrency(effectiveSaleComm)}
                  </p>
                  {!isInitial ? (
                    <p className="text-[10px] font-bold text-slate-500 mt-1">
                      Comisión inicial: <span className="font-mono text-slate-700">{formatCurrency(value)}</span> ({baseRateLabel})
                    </p>
                  ) : (
                    <p className="text-[10px] font-bold text-slate-400 mt-1">
                      Comisión base de lista ({baseRateLabel})
                    </p>
                  )}
                </div>
              )}

              {showCreditCommission && (
                <div className="bg-pink-50/70 p-4 rounded-2xl border border-pink-100 shadow-sm transform hover:scale-[1.01] transition-all">
                  <div className="flex items-center justify-between mb-1.5 gap-2">
                    <p className="text-xs sm:text-sm font-black uppercase tracking-[0.16em] text-pink-950">
                      Comisión por Crédito
                    </p>
                    <span className="bg-pink-200/80 text-pink-900 text-[10px] sm:text-xs font-black uppercase px-2.5 py-1 rounded-full tracking-wider border border-pink-200">
                      0.4% Aliados
                    </span>
                  </div>
                  <p className="text-xl xs:text-2xl md:text-xl lg:text-2xl font-black text-pink-700 tracking-tight break-all font-mono">
                    {formatCurrency(effectiveCreditComm)}
                  </p>
                  <p className="text-[10px] sm:text-[11px] text-pink-850 uppercase font-black tracking-wider mt-2.5 block leading-normal">
                    ✨ Aplica al gestionar el referido en el sistema
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

PricingCommissionCards.displayName = 'PricingCommissionCards';
